import React, { useRef, useState, useEffect } from "react";
import { FiDownload, FiPlus } from "react-icons/fi";
import { toPng } from "html-to-image";
import { SimpleButton } from "../../components/button.jsx/Btn";
import { OrganogramTree } from "../../components/organogram/OrganogramTree";
import { OrganogramEditModal } from "../../components/organogram/OrganogramEditModal";
import { OrganogramDeleteModal } from "../../components/organogram/OrganogramDeleteModal";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import axios from "axios";
import { getApiBaseUrl } from "../../data/authStorage";

const OrgChart = () => {
  const [organogramData, setOrganogramData] = useState([]);
  const [allPositions, setAllPositions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [parentPositionForChild, setParentPositionForChild] = useState(null);
  const chartExportRef = useRef(null);

  // Load organogram data
  const loadOrganogramData = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${getApiBaseUrl()}/organizations/organogram/tree`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setOrganogramData(response.data.data || []);
      setErrorMessage(null);
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || "Failed to load organogram data";
      setErrorMessage(errorMsg);
      setOrganogramData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load all positions for parent position dropdown
  const loadAllPositions = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${getApiBaseUrl()}/organizations/positions`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Flatten positions from response
      const positions = (response.data.positions || []).map((pos) => ({
        id: pos.id,
        positionName: pos.positionName || pos.name,
        positionDescription: pos.positionDescription || pos.description,
      }));

      setAllPositions(positions);
    } catch (error) {
      console.error("Failed to load positions:", error);
    }
  };

  useEffect(() => {
    loadOrganogramData();
    loadAllPositions();
  }, []);

  // Handle opening edit modal
  const handleEditPosition = (position) => {
    if (position) {
      setSelectedPosition(position);
      setParentPositionForChild(null);
    }
    setEditModalOpen(true);
  };

  // Handle opening delete modal
  const handleDeletePosition = (position) => {
    setSelectedPosition(position);
    setDeleteModalOpen(true);
  };

  // Handle adding child position
  const handleAddChildPosition = (parentPosition) => {
    setSelectedPosition(null);
    setParentPositionForChild(parentPosition);
    setEditModalOpen(true);
  };

  // Handle save position (create or update)
  const handleSavePosition = async (formData) => {
    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      const token = localStorage.getItem("authToken");

      const requestData = {
        positionName: formData.positionName,
        positionDescription: formData.positionDescription,
        parentPositionId:
          parentPositionForChild?.id || formData.parentPositionId || null,
      };

      if (selectedPosition) {
        // Update existing position
        await axios.patch(
          `${getApiBaseUrl()}/organizations/organogram/positions/${selectedPosition.id}`,
          requestData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setSuccessMessage("Position updated successfully");
      } else {
        // Create new position
        await axios.post(
          `${getApiBaseUrl()}/organizations/organogram/positions`,
          requestData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setSuccessMessage("Position created successfully");
      }

      // Reload data
      await loadOrganogramData();
      await loadAllPositions();
      setEditModalOpen(false);
      setSelectedPosition(null);
      setParentPositionForChild(null);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || "Failed to save position";
      setErrorMessage(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete position
  const handleConfirmDelete = async () => {
    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      const token = localStorage.getItem("authToken");

      await axios.delete(
        `${getApiBaseUrl()}/organizations/organogram/positions/${selectedPosition.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setSuccessMessage("Position deleted successfully");
      await loadOrganogramData();
      await loadAllPositions();
      setDeleteModalOpen(false);
      setSelectedPosition(null);

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || "Failed to delete position";
      setErrorMessage(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle drag and drop reordering
  const handleReorderPosition = async (draggedPositionId, targetPositionId) => {
    try {
      setErrorMessage(null);
      const token = localStorage.getItem("authToken");

      await axios.patch(
        `${getApiBaseUrl()}/organizations/organogram/positions/${draggedPositionId}/hierarchy`,
        { parentPositionId: targetPositionId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setSuccessMessage("Position hierarchy updated");
      await loadOrganogramData();

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || "Failed to update hierarchy";
      setErrorMessage(errorMsg);
    }
  };

  const handleDownloadChartImage = async () => {
    if (!chartExportRef.current) {
      return;
    }

    try {
      setIsDownloadingImage(true);
      setErrorMessage(null);

      const chartNode = chartExportRef.current;
      const exportWidth = Math.max(
        chartNode.scrollWidth,
        chartNode.clientWidth,
      );
      const exportHeight = Math.max(
        chartNode.scrollHeight,
        chartNode.clientHeight,
      );

      const dataUrl = await toPng(chartNode, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#f9fafb",
        width: exportWidth,
        height: exportHeight,
        style: {
          width: `${exportWidth}px`,
          height: `${exportHeight}px`,
        },
      });

      const timestamp = new Date().toISOString().slice(0, 10);
      const downloadLink = document.createElement("a");
      downloadLink.download = `organogram-${timestamp}.png`;
      downloadLink.href = dataUrl;
      downloadLink.click();

      setSuccessMessage("Organogram downloaded successfully");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      setErrorMessage("Failed to download organogram image. Please try again.");
    } finally {
      setIsDownloadingImage(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex flex-col items-center bg-white mx-auto max-w-8xl rounded-xl min-h-screen py-10 p-6">
        <div className="w-full">
          <PageIntroNav
            breadcrumbs={[
              { label: "Company Settings", path: "/company-settings" },
              { label: "Organogram" },
            ]}
            title="Organizational Chart"
            subtitle="Build a top-down hierarchy, add multiple staff under each cadre, and keep reporting lines clear."
            fallbackPath="/company-settings"
            backLabel="Back to Company Settings"
          />

          {/* Header */}
          <div className="flex items-center justify-end gap-3 mb-8">
            <SimpleButton
              onClick={handleDownloadChartImage}
              disabled={
                isLoading || isDownloadingImage || organogramData.length === 0
              }
              className="px-6 py-2 rounded border border-blue-600 flex justify-center items-center text-blue-600 disabled:opacity-60 disabled:cursor-not-allowed gap-2"
            >
              <FiDownload size={18} />
              {isDownloadingImage ? "Downloading..." : "Download Image"}
            </SimpleButton>

            <SimpleButton
              onClick={() => handleAddChildPosition(null)}
              className="px-6 py-2 rounded bg-blue-600 flex justify-center items-center text-white hover:bg-blue-700 transition-colors gap-2"
            >
              <FiPlus size={18} />
              Add Position
            </SimpleButton>
          </div>

          {/* Messages */}
          {errorMessage && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{errorMessage}</p>
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-700 text-sm">{successMessage}</p>
            </div>
          )}

          {/* Organogram Tree */}
          <div
            ref={chartExportRef}
            className="bg-gray-50 rounded-lg border border-gray-200 p-6"
          >
            <OrganogramTree
              data={organogramData}
              onEdit={handleEditPosition}
              onDelete={handleDeletePosition}
              onAddChild={handleAddChildPosition}
              onReorder={handleReorderPosition}
              isLoading={isLoading}
              error={errorMessage}
            />
          </div>

          {/* Info Box */}
          {!isLoading && organogramData.length > 0 && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800 text-sm">
                💡 <strong>Tip:</strong> You can drag and drop positions to
                reorganize the hierarchy. Click the edit icon to change position
                details.
              </p>
            </div>
          )}
        </div>

        {/* Edit Modal */}
        <OrganogramEditModal
          isOpen={editModalOpen}
          position={selectedPosition}
          allPositions={allPositions}
          onSave={handleSavePosition}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedPosition(null);
            setParentPositionForChild(null);
          }}
          isSubmitting={isSubmitting}
          errorMessage={errorMessage}
        />

        {/* Delete Modal */}
        <OrganogramDeleteModal
          isOpen={deleteModalOpen}
          position={selectedPosition}
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setDeleteModalOpen(false);
            setSelectedPosition(null);
          }}
          isSubmitting={isSubmitting}
        />
      </div>
    </div>
  );
};

export default OrgChart;
